import { useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useStagingMode } from "@/lib/staging";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

// "Reset staging data" — admin only, and only when the backend reports
// staging mode. The backend re-checks both before doing anything.
export default function StagingResetButton() {
  const { user } = useAuth();
  const staging = useStagingMode();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  if (!staging || user?.role !== "admin") return null;

  const reset = async () => {
    setBusy(true);
    try {
      await api.post("/staging/reset");
      toast.success("Staging reset complete");
      setOpen(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Staging reset failed");
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-red-300 text-red-700 hover:bg-red-50"
        onClick={() => { setConfirmText(""); setOpen(true); }}
        data-testid="staging-reset-btn"
      >
        <RotateCcw className="w-4 h-4 mr-1" /> Reset staging data
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
        <DialogContent data-testid="staging-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset staging data?</DialogTitle>
            <DialogDescription>
              This wipes the staging database and restores it from the baseline snapshot.
              Every change made on staging since the last reset is lost. The live site is not affected.
              Type <b>RESET</b> to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="RESET"
            disabled={busy}
            data-testid="staging-reset-confirm-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={confirmText !== "RESET" || busy}
              onClick={reset}
              data-testid="staging-reset-confirm-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Reset staging data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
